// =====================================================================
// SMART FORM ACTIONS — one-click Save&Submit / Cancel&Delete / Cancel&Edit
// =====================================================================
// Adds intelligent action buttons to submittable doctypes:
//
//   docstatus 0 (Draft)     → "Save & Submit"        (green)
//   docstatus 1 (Submitted) → "Cancel & Delete"      (red)
//                           → "Cancel & Edit (Amend)" (amber)
//
// Toggled per-button from Theme Lab → "Smart Actions" section.
// Persisted client-side in localStorage (no schema change, zero migration
// risk — deliberately avoids the bugs from server-field approaches).
//
// Robustness: every action is wrapped, uses Frappe's official frm.* APIs
// (savesubmit / savecancel / amend_doc / client.delete), reloads/route to
// avoid stale state ("acts like a clear-cache"), and never double-binds.
// =====================================================================

import { userKey } from "../utils/storage.js";

// Per-user key — smart-action toggles must not bleed between users on a
// shared browser (Theme Lab user-scoping).
const LS_KEY = () => userKey("form_actions");
const DEFAULTS = { save_submit: 1, cancel_delete: 1, cancel_edit: 1 };
const BTN_CLASS = "e16-smart-act";

function readPrefs() {
	try {
		const raw = localStorage.getItem(LS_KEY());
		if (!raw) return { ...DEFAULTS };
		const p = JSON.parse(raw);
		return {
			save_submit: p.save_submit ? 1 : 0,
			cancel_delete: p.cancel_delete ? 1 : 0,
			cancel_edit: p.cancel_edit ? 1 : 0,
		};
	} catch (e) {
		return { ...DEFAULTS };
	}
}

function writePrefs(p) {
	try { localStorage.setItem(LS_KEY(), JSON.stringify(p)); } catch (e) {}
}

function L(ar, en) {
	const lang = document.documentElement.getAttribute("lang");
	return lang === "ar" ? ar : en;
}

// ---- safe helpers -------------------------------------------------------

function toast(msg, indicator) {
	try { frappe.show_alert({ message: msg, indicator: indicator || "blue" }, 5); } catch (e) {}
}

// Route to list and force a clean reload of cached doc state.
function gotoListClean(doctype) {
	try {
		frappe.model && frappe.model.clear_doc && frappe.model.clear_doc;
	} catch (e) {}
	try {
		frappe.set_route("List", doctype);
	} catch (e) {
		window.location.href = "/app/" + frappe.router.slug(doctype);
	}
}

// ---- the three smart actions -------------------------------------------

function actSaveSubmit(frm) {
	// frm.savesubmit() already shows the native confirm + saves + submits.
	try {
		frm.savesubmit();
	} catch (e) {
		toast(L("تعذّر الحفظ والترحيل", "Could not save & submit"), "red");
		console.warn("[e16] save&submit", e);
	}
}

function actCancelDelete(frm) {
	const dt = frm.doctype;
	const dn = frm.docname;
	frappe.confirm(
		L(
			`سيتم <b>إلغاء</b> ثم <b>حذف</b> المستند ${frappe.utils.escape_html(dn)} نهائياً. متابعة؟`,
			`This will <b>Cancel</b> then permanently <b>Delete</b> ${frappe.utils.escape_html(dn)}. Continue?`
		),
		() => {
			// Step 1: cancel
			frm.savecancel(
				undefined,
				() => {
					// Step 2: delete (doc is now docstatus 2 → deletable)
					frappe.call({
						method: "frappe.client.delete",
						args: { doctype: dt, name: dn },
						freeze: true,
						freeze_message: L("جارٍ الحذف…", "Deleting…"),
					}).then(() => {
						toast(L("تم الإلغاء والحذف بنجاح", "Cancelled & deleted"), "green");
						gotoListClean(dt);
					}).catch((err) => {
						toast(L("تم الإلغاء، لكن تعذّر الحذف", "Cancelled, but delete failed"), "orange");
						console.warn("[e16] delete", err);
					});
				},
				() => toast(L("تعذّر الإلغاء", "Cancel failed"), "red")
			);
		}
	);
}

function actCancelEdit(frm) {
	// Cancel, then open an editable amended copy of the original doc.
	frappe.confirm(
		L(
			"سيتم <b>إلغاء</b> المستند ثم فتح نسخة قابلة للتعديل (تعديل/Amend). متابعة؟",
			"This will <b>Cancel</b> the document then open an editable amended copy. Continue?"
		),
		() => {
			frm.savecancel(
				undefined,
				() => {
					try {
						// amend_doc creates a new editable copy & routes to it
						frm.reload_doc().then(() => frm.amend_doc());
					} catch (e) {
						try { frm.amend_doc(); } catch (e2) {
							toast(L("تعذّر فتح التعديل", "Could not open amend"), "red");
						}
					}
				},
				() => toast(L("تعذّر الإلغاء", "Cancel failed"), "red")
			);
		}
	);
}

// ---- button injection ---------------------------------------------------

function clearOurButtons(frm) {
	try {
		frm.page.wrapper.find("." + BTN_CLASS).remove();
	} catch (e) {}
}

function addSmartButton(frm, label, cssVariant, handler) {
	let $btn;
	try {
		$btn = frm.add_custom_button(label, () => handler(frm));
	} catch (e) {
		return;
	}
	if (!$btn || !$btn.length) return;
	$btn.removeClass("btn-default")
		.addClass(`${BTN_CLASS} ${BTN_CLASS}--${cssVariant}`);
}

function decorate(frm) {
	if (!frm || !frm.doc || !frm.meta) return;

	// HARD GUARD: never touch the page while Frappe is mid-operation —
	// a grid-row editor is open, the form is frozen, or a modal/dialog
	// is up. Manipulating the toolbar then can collide with Frappe's
	// grid render and leave a stuck #freeze (blank page + hang).
	try {
		if (
			document.querySelector(".grid-row-open") ||
			document.querySelector("#freeze") ||
			(window.frappe && frappe.dom && frappe.dom.freeze_count > 0)
		) {
			return;
		}
	} catch (e) {
		return;
	}

	const prefs = readPrefs();

	clearOurButtons(frm);

	const isSubmittable = !!frm.meta.is_submittable;
	if (!isSubmittable) return;

	const ds = frm.doc.docstatus;

	// Draft → Save & Submit
	if (ds === 0 && prefs.save_submit && !frm.is_new()) {
		addSmartButton(
			frm,
			L("حفظ و ترحيل", "Save & Submit"),
			"submit",
			actSaveSubmit
		);
	}

	// Submitted → Cancel & Delete  +  Cancel & Edit
	if (ds === 1) {
		if (prefs.cancel_delete) {
			addSmartButton(
				frm,
				L("إلغاء و حذف", "Cancel & Delete"),
				"delete",
				actCancelDelete
			);
		}
		if (prefs.cancel_edit) {
			addSmartButton(
				frm,
				L("إلغاء و تعديل", "Cancel & Edit"),
				"edit",
				actCancelEdit
			);
		}
	}
}

// ---- global form hook (patched ONCE) ------------------------------------

let patched = false;

function installHook() {
	if (patched) return;
	if (!window.frappe || !frappe.ui || !frappe.ui.form || !frappe.ui.form.Form) return;
	const proto = frappe.ui.form.Form.prototype;
	if (!proto || proto.__e16_actions_patched) return;

	const origRefresh = proto.refresh;
	proto.refresh = function (...args) {
		const out = origRefresh.apply(this, args);
		// Defer so Frappe finishes building its own toolbar first
		setTimeout(() => {
			try { decorate(this); } catch (e) { console.warn("[e16] form-actions", e); }
		}, 60);
		return out;
	};
	proto.__e16_actions_patched = true;
	patched = true;
}

// =====================================================================
// RE-ENABLED — safe to run thanks to two later defences:
//   1. `_safety.scss` permanently nulls every containing-block/stacking
//      creator on every grid ancestor → the historic Child Table trap
//      cannot recur from any global patch.
//   2. `grid_guard.js` inline-forces the row editor above the freeze
//      with z-index 2147483000 — works regardless of any CSS or JS.
//   3. `decorate()` itself bails the moment a `.grid-row-open` exists
//      or freeze_count > 0 (see HARD GUARD above) — it never runs
//      during grid operations.
// Smart-action buttons (Save & Submit / Cancel & Delete / Cancel & Edit)
// are restored. Toggle them anytime from Theme Lab → Smart Actions.
// =====================================================================
export const formActions = {
	mount() {
		if (window.frappe && frappe.ui && frappe.ui.form && frappe.ui.form.Form) {
			installHook();
		} else {
			let tries = 0;
			const iv = setInterval(() => {
				tries++;
				if (window.frappe && frappe.ui && frappe.ui.form && frappe.ui.form.Form) {
					installHook();
					clearInterval(iv);
				} else if (tries > 80) {
					clearInterval(iv);
				}
			}, 250);
		}
	},
	getPrefs: readPrefs,
	setPrefs(p) {
		try {
			writePrefs(p);
			// re-decorate the current open form immediately when a toggle changes
			if (window.cur_frm) decorate(window.cur_frm);
		} catch (e) {}
	},
};
